/**
 * FauxDS Tag - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Tag component for prototyping.
 * It is NOT the real @faire/slate Tag and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Tag, see: @faire/slate/components/tag
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 13595:8394 (Slate Tag)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Neutral, Warning, Success, Critical
 * - All states verified: Default, Active, Hover
 * - Dismissible support
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

export type TagVariant =
  | "neutral"
  | "warning"
  | "success"
  | "critical"
  | "fairePay";
export type TagState = "Default" | "Active" | "Hover";

export interface TagProps {
  /**
   * Label text for the tag
   * @default "Label"
   */
  label?: string;
  /**
   * **playground only**
   * Whether the tag is focused
   * @default false
   */
  focused?: boolean;
  /**
   * Accessibility label for screen readers
   * @default "Label"
   */
  "aria-label"?: string;
  /**
   * Tag variant
   * @default "Neutral"
   */
  variant?: TagVariant;
  /**
   * **playground only**
   * Tag state
   * @default "Default"
   */
  state?: TagState;
  /**
   * Dismiss handler (called when close button is clicked). When provided, the dismiss button is shown (neutral variant only).
   */
  onDismiss?: () => void;
  /**
   * **playground only**
   * Click handler
   */
  onClick?: () => void;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Close Icon (X icon)
 */
const CloseIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 3L9 9M9 3L3 9"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Tag - FauxDS (recreated from Figma MCP)
 *
 * Tags contain a descriptive word or phrase associated with a parent object,
 * usually providing a description or value of that object. Tags can be static
 * (pre-added to a page) or dynamic (interactive and user-selected).
 *
 * Usage tips:
 * - Tags can be static or dynamic.
 * - Tags can be grouped into 'Tag Groups' that show multiple related components together.
 * - Choose the right variant for your use case. Neutral variants are interactive while Status-based variants are not.
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
export default function Tag({
  label: labelText = "Label",
  focused = false,
  "aria-label": ariaLabel,
  variant = "neutral",
  state = "Default",
  onDismiss,
  onClick,
  className = "",
}: TagProps) {
  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case "warning":
        return {
          bg: "bg-[var(--slate-color-message-surface-warning)]",
          text: "text-[var(--slate-color-message-text-warning)]",
        };
      case "success":
        return {
          bg: "bg-[var(--slate-color-message-surface-success)]",
          text: "text-[var(--slate-color-message-text-success)]",
        };
      case "critical":
        return {
          bg: "bg-[var(--slate-color-message-surface-critical)]",
          text: "text-[var(--slate-color-message-text-critical)]",
        };
      case "fairePay":
        return {
          bg: "bg-[#e9eefb]",
          text: "text-[#333]",
        };
      default: // Neutral
        return {
          bg: "bg-[var(--slate-color-grey-300)]",
          text: "text-[var(--slate-color-action-text-default)]",
        };
    }
  };

  // Get state styles (only applies to Neutral variant)
  const getStateStyles = () => {
    if (variant !== "neutral") {
      return {
        hover: "",
        active: "",
      };
    }

    switch (state) {
      case "Hover":
        return {
          hover: "hover:bg-[var(--slate-color-grey-400)]",
          active: "",
        };
      case "Active":
        return {
          hover: "",
          active: "bg-[var(--slate-color-grey-400)]",
        };
      default:
        return {
          hover: "hover:bg-[var(--slate-color-grey-400)]",
          active: "",
        };
    }
  };

  const variantStyles = getVariantStyles();
  const stateStyles = getStateStyles();

  // Only show dismiss button for Neutral variant when onDismiss is provided
  const canDismiss = variant === "neutral" && !!onDismiss;
  const isInteractive = variant === "neutral" && (!!onClick || canDismiss);
  const showPointer = variant === "neutral" && !!onClick;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss?.();
  };

  return (
    <span
      onClick={onClick}
      className={cn(
        "inline-flex h-[24px] items-center rounded-[var(--slate-radius-component-tag)] px-[8px]",
        variantStyles.bg,
        variantStyles.text,
        stateStyles.hover,
        stateStyles.active,
        showPointer ? "cursor-pointer" : "",
        focused
          ? "ring-2 ring-[var(--slate-color-action-border-focus-ring)] ring-offset-1"
          : "",
        "transition-colors duration-150",
        className
      )}
      style={{
        fontFamily: "var(--slate-typography-family-sans-serif)",
        fontSize: "var(--slate-typography-font-size-100)",
        fontWeight: "var(--slate-typography-weight-regular)",
        lineHeight: "var(--slate-typography-line-height-100)",
        letterSpacing: "var(--slate-typography-letter-spacing-200)",
      }}
      aria-label={ariaLabel}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive && !focused ? 0 : undefined}
    >
      <span className="truncate">{labelText}</span>
      {canDismiss && (
        <button
          type="button"
          onClick={handleDismiss}
          className={cn(
            "ml-[8px] flex cursor-pointer items-center justify-center rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)] focus-visible:ring-offset-1",
            variantStyles.text
          )}
          aria-label={`Remove ${labelText}`}
        >
          <CloseIcon />
        </button>
      )}
    </span>
  );
}
